// services/accommodationService.js - Version migrée vers apiClient
import { get, post, put, del, uploadFile } from '../../../utils/apiClient'

// NOUVEAU : Fonction centralisée de gestion d'erreurs
const handleApiError = (error, defaultMessage) => {
  console.error(defaultMessage, error)
  let errorMessage = defaultMessage

  if (error.response) {
    if (error.response.status === 413) {
      errorMessage =
        'Erreur: Fichier(s) trop volumineux. La taille totale ne doit pas dépasser 10MB.'
    } else if (error.response.data && error.response.data.message) {
      errorMessage = error.response.data.message
    } else if (error.response.status === 401) {
      errorMessage = 'Session expirée. Veuillez vous reconnecter.'
    } else if (error.response.status === 403) {
      errorMessage = "Vous n'avez pas les permissions nécessaires pour cette action."
    } else if (error.response.status === 404) {
      errorMessage = 'Ressource non trouvée.'
    } else {
      errorMessage = `Erreur ${error.response.status}: ${defaultMessage}`
    }
  } else if (error.request) {
    errorMessage = 'Erreur de connexion au serveur. Veuillez vérifier votre connexion Internet.'
  } else {
    errorMessage = `Erreur: ${error.message || defaultMessage}`
  }

  return errorMessage
}

const accommodationService = {
  // CORRIGÉ : Récupérer tous les hébergements avec pagination et filtres
  getAccommodations: async (page = 1, limit = 10, filters = {}, searchTerm = '') => {
    try {
      let queryParams = `page=${page}&limit=${limit}`

      if (searchTerm && searchTerm.trim()) {
        queryParams += `&search=${encodeURIComponent(searchTerm.trim())}`
      }

      // Gestion unifiée des prix
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          let paramKey

          // Mapping des clés de filtres
          if (key === 'priceMin') {
            paramKey = 'min_price'
          } else if (key === 'priceMax') {
            paramKey = 'max_price'
          } else if (key === 'minBedrooms') {
            paramKey = 'min_bedrooms'
          } else if (key === 'minBathrooms') {
            paramKey = 'min_bathrooms'
          } else {
            paramKey = key
          }

          queryParams += `&${paramKey}=${encodeURIComponent(value)}`
        }
      })

      console.log('Requête GET accommodations:', `/accommodations?${queryParams}`)

      return await get(`/accommodations?${queryParams}`)
    } catch (error) {
      const errorMessage = handleApiError(error, 'Erreur lors de la récupération des hébergements')
      throw new Error(errorMessage)
    }
  },

  // CORRIGÉ : Récupérer un hébergement par son ID
  getAccommodationById: async (id) => {
    try {
      if (!id) {
        throw new Error("ID d'hébergement manquant")
      }

      console.log('Requête GET accommodation:', `/accommodations/${id}`)

      return await get(`/accommodations/${id}`)
    } catch (error) {
      const errorMessage = handleApiError(
        error,
        `Erreur lors de la récupération de l'hébergement #${id}`,
      )
      throw new Error(errorMessage)
    }
  },

  // CORRIGÉ : Créer un nouvel hébergement
  createAccommodation: async (accommodationData, photos = []) => {
    try {
      console.log('=== CRÉATION HÉBERGEMENT SERVICE ===')
      console.log('Données:', accommodationData)
      console.log('Photos:', photos.length)

      // Validation côté client
      if (!accommodationData.title || accommodationData.title.trim().length < 3) {
        throw new Error('Le titre doit contenir au moins 3 caractères')
      }

      if (!accommodationData.price || parseFloat(accommodationData.price) <= 0) {
        throw new Error('Un prix valide est requis')
      }

      const formData = new FormData()

      // Ajouter les champs texte
      Object.keys(accommodationData).forEach((key) => {
        let value = accommodationData[key]

        if (key === 'availabilities') {
          // ✅ CORRECTION PRINCIPALE : Toujours traiter les availabilities
          if (Array.isArray(value)) {
            if (value.length > 0) {
              // Nettoyer et convertir les dates pour un tableau non-vide
              const cleanedAvailabilities = value.map((avail) => ({
                start_date:
                  avail.start_date instanceof Date
                    ? avail.start_date.toISOString()
                    : avail.start_date,
                end_date:
                  avail.end_date instanceof Date ? avail.end_date.toISOString() : avail.end_date,
                is_available: Boolean(avail.is_available),
                notes: String(avail.notes || ''),
              }))
              value = JSON.stringify(cleanedAvailabilities)
              console.log('📤 Availabilities avec données envoyées:', value)
            } else {
              // Tableau vide mais valide
              value = JSON.stringify([])
              console.log('📤 Availabilities tableau vide envoyé')
            }
          } else if (value === null || value === undefined) {
            // Cas où availabilities n'est pas défini
            value = JSON.stringify([])
            console.log('📤 Availabilities undefined converti en tableau vide')
          } else {
            // Cas où availabilities est déjà une string ou autre type
            console.log('📤 Availabilities reçu comme:', typeof value, value)
            // Essayer de parser si c'est une string, sinon envoyer tableau vide
            try {
              if (typeof value === 'string') {
                const parsed = JSON.parse(value)
                if (Array.isArray(parsed)) {
                  value = JSON.stringify(parsed) // Re-stringify pour cohérence
                } else {
                  value = JSON.stringify([])
                }
              } else {
                value = JSON.stringify([])
              }
            } catch (error) {
              console.warn('Erreur parsing availabilities, envoi tableau vide:', error)
              value = JSON.stringify([])
            }
          }
        } else if (key === 'amenities') {
          if (typeof value === 'object') {
            value = JSON.stringify(value)
          }
        } else if (value === null || value === undefined) {
          value = ''
        }

        formData.append(key, value)
      })

      // Validation et ajout des photos
      let totalSize = 0
      photos.forEach((file) => {
        if (!(file instanceof File)) {
          throw new Error('Fichier invalide détecté')
        }

        // Vérifier le type de fichier
        const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
        if (!validTypes.includes(file.type)) {
          throw new Error(
            `Le fichier ${file.name} n'est pas une image valide (JPG, PNG, GIF, WebP)`,
          )
        }

        // Vérifier la taille
        if (file.size > 5 * 1024 * 1024) {
          throw new Error(`Le fichier ${file.name} est trop volumineux (max 5MB)`)
        }

        totalSize += file.size
        // ✅ CORRECTION SYSTÈME UNIFIÉ: Utiliser 'images' au lieu de 'photos'
        formData.append('images', file)
      })

      // Vérifier la taille totale
      const MAX_TOTAL_SIZE = 15 * 1024 * 1024 // 15 MB
      if (totalSize > MAX_TOTAL_SIZE) {
        throw new Error(
          `La taille totale des fichiers (${(totalSize / (1024 * 1024)).toFixed(2)} MB) dépasse la limite autorisée (15 MB).`,
        )
      }

      console.log('Envoi requête POST...')

      // ✅ CORRECTION PHASE 5: Utiliser onProgress au lieu de onUploadProgress
      const data = await uploadFile('/accommodations', formData, {
        method: 'POST',
        onProgress: (percentCompleted) => {
          console.log(`Accommodation creation: ${percentCompleted}%`)
        },
      })

      console.log('Création réussie:', data)
      return data
    } catch (error) {
      const errorMessage = handleApiError(error, "Erreur lors de la création de l'hébergement")
      throw new Error(errorMessage)
    }
  },

  updateAccommodation: async (id, accommodationData, options = {}) => {
    try {
      if (!id) {
        throw new Error("ID d'hébergement manquant")
      }

      const removeAllPhotos = Boolean(options.removeAllPhotos)
      const existingPhotos = Array.isArray(options.existingPhotos) ? options.existingPhotos : []
      const newPhotos = Array.isArray(options.newPhotos) ? options.newPhotos : []
      const deletedPhotoIds = Array.isArray(options.deletedPhotoIds) ? options.deletedPhotoIds : []

      console.log('Variables extraites:', {
        removeAllPhotos,
        existingPhotosCount: existingPhotos.length,
        newPhotosCount: newPhotos.length,
        deletedPhotoIds,
      })

      // Validation des données
      if (!accommodationData.title || accommodationData.title.trim().length < 3) {
        throw new Error('Le titre doit contenir au moins 3 caractères')
      }

      if (!accommodationData.price || parseFloat(accommodationData.price) <= 0) {
        throw new Error('Un prix valide est requis')
      }

      const formData = new FormData()

      // CORRECTION : Traitement des availabilities
      Object.keys(accommodationData).forEach((key) => {
        let value = accommodationData[key]

        if (key === 'availabilities') {
          // ✅ CORRECTION PRINCIPALE : Toujours traiter les availabilities
          if (Array.isArray(value)) {
            if (value.length > 0) {
              // Nettoyer et convertir les dates pour un tableau non-vide
              const cleanedAvailabilities = value.map((avail) => ({
                start_date:
                  avail.start_date instanceof Date
                    ? avail.start_date.toISOString()
                    : avail.start_date,
                end_date:
                  avail.end_date instanceof Date ? avail.end_date.toISOString() : avail.end_date,
                is_available: Boolean(avail.is_available),
                notes: String(avail.notes || ''),
              }))
              value = JSON.stringify(cleanedAvailabilities)
              console.log('📤 Availabilities avec données envoyées:', value)
            } else {
              // Tableau vide mais valide
              value = JSON.stringify([])
              console.log('📤 Availabilities tableau vide envoyé')
            }
          } else if (value === null || value === undefined) {
            // Cas où availabilities n'est pas défini
            value = JSON.stringify([])
            console.log('📤 Availabilities undefined converti en tableau vide')
          } else {
            // Cas où availabilities est déjà une string ou autre type
            console.log('📤 Availabilities reçu comme:', typeof value, value)
            // Essayer de parser si c'est une string, sinon envoyer tableau vide
            try {
              if (typeof value === 'string') {
                const parsed = JSON.parse(value)
                if (Array.isArray(parsed)) {
                  value = JSON.stringify(parsed) // Re-stringify pour cohérence
                } else {
                  value = JSON.stringify([])
                }
              } else {
                value = JSON.stringify([])
              }
            } catch (error) {
              console.warn('Erreur parsing availabilities, envoi tableau vide:', error)
              value = JSON.stringify([])
            }
          }
        } else if (key === 'amenities') {
          if (typeof value === 'object') {
            value = JSON.stringify(value)
          }
        } else if (value === null || value === undefined) {
          value = ''
        }

        formData.append(key, value)
      })

      // Gestion des photos existantes
      formData.append('removeAllPhotos', removeAllPhotos ? 'true' : 'false')

      if (existingPhotos.length > 0) {
        const photoData = existingPhotos.map((photo) => ({
          id: photo.id,
          is_main: Boolean(photo.is_main),
        }))
        formData.append('existingPhotos', JSON.stringify(photoData))
        console.log('📸 Photos existantes conservées:', photoData)
      }

      // AJOUT : Envoyer les IDs des photos à supprimer
      if (deletedPhotoIds.length > 0) {
        formData.append('deletedPhotoIds', JSON.stringify(deletedPhotoIds))
        console.log('🗑️ Photos à supprimer (IDs):', deletedPhotoIds)
      }

      // Validation et ajout des nouvelles photos
      let totalSize = 0
      for (const file of newPhotos) {
        if (!(file instanceof File)) {
          throw new Error('Fichier invalide détecté')
        }

        const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
        if (!validTypes.includes(file.type)) {
          throw new Error(`Le fichier ${file.name} n'est pas une image valide`)
        }

        if (file.size > 5 * 1024 * 1024) {
          throw new Error(`Le fichier ${file.name} est trop volumineux (max 5MB)`)
        }

        totalSize += file.size
        // ✅ CORRECTION SYSTÈME UNIFIÉ: Utiliser 'images' au lieu de 'photos'
        formData.append('images', file)
      }

      const MAX_TOTAL_SIZE = 15 * 1024 * 1024 // 15 MB
      if (totalSize > MAX_TOTAL_SIZE) {
        throw new Error(
          `Taille totale trop importante: ${(totalSize / (1024 * 1024)).toFixed(2)} MB > 15 MB`,
        )
      }

      // ✅ CORRECTION PHASE 5: Utiliser onProgress au lieu de onUploadProgress
      const data = await uploadFile(`/accommodations/${id}`, formData, {
        method: 'PUT',
        onProgress: (percentCompleted) => {
          console.log(`Accommodation update: ${percentCompleted}%`)
        },
      })

      console.log('✅ Mise à jour réussie')
      return data
    } catch (error) {
      console.error('❌ Erreur updateAccommodation:', error)

      let errorMessage = 'Erreur lors de la mise à jour'
      if (error.response) {
        errorMessage = error.response.data?.message || `Erreur ${error.response.status}`
      } else if (error.request) {
        errorMessage = 'Erreur de connexion au serveur'
      } else {
        errorMessage = error.message || errorMessage
      }

      throw new Error(errorMessage)
    }
  },

  // CORRIGÉ : Supprimer un hébergement
  deleteAccommodation: async (id) => {
    try {
      if (!id) {
        throw new Error("ID d'hébergement manquant")
      }

      console.log('Suppression hébergement:', id)

      const data = await del(`/accommodations/${id}`)

      console.log('Suppression réussie')
      return data
    } catch (error) {
      const errorMessage = handleApiError(
        error,
        `Erreur lors de la suppression de l'hébergement #${id}`,
      )
      throw new Error(errorMessage)
    }
  },

  // NOUVEAU : Récupérer mes hébergements
  getMyAccommodations: async (page = 1, limit = 10, status = 'all') => {
    try {
      let queryParams = `page=${page}&limit=${limit}`
      if (status && status !== 'all') {
        queryParams += `&status=${encodeURIComponent(status)}`
      }

      return await get(`/accommodations/my-accommodations?${queryParams}`)
    } catch (error) {
      const errorMessage = handleApiError(
        error,
        'Erreur lors de la récupération de vos hébergements',
      )
      throw new Error(errorMessage)
    }
  },

  // NOUVEAU : Dashboard propriétaire
  getOwnerDashboard: async () => {
    try {
      return await get('/accommodations/owner-dashboard')
    } catch (error) {
      const errorMessage = handleApiError(error, 'Erreur lors de la récupération du dashboard')
      throw new Error(errorMessage)
    }
  },

  // NOUVEAU : Recherche avancée
  searchAccommodations: async (searchParams) => {
    try {
      return await post('/accommodations/search', searchParams)
    } catch (error) {
      const errorMessage = handleApiError(error, "Erreur lors de la recherche d'hébergements")
      throw new Error(errorMessage)
    }
  },

  // NOUVEAU : Validation admin
  validateAccommodation: async (id, status = 'active') => {
    try {
      if (!id) {
        throw new Error("ID d'hébergement manquant")
      }

      return await put(`/accommodations/${id}/validate`, { status })
    } catch (error) {
      const errorMessage = handleApiError(
        error,
        `Erreur lors de la validation de l'hébergement #${id}`,
      )
      throw new Error(errorMessage)
    }
  },

  // NOUVEAU : Gestion des photos
  setMainPhoto: async (accommodationId, photoId) => {
    try {
      if (!accommodationId || !photoId) {
        throw new Error("ID d'hébergement et de photo requis")
      }

      return await put(`/accommodations/${accommodationId}/photos/${photoId}/main`, {})
    } catch (error) {
      const errorMessage = handleApiError(
        error,
        'Erreur lors de la définition de la photo principale',
      )
      throw new Error(errorMessage)
    }
  },

  deletePhoto: async (accommodationId, photoId) => {
    try {
      if (!accommodationId || !photoId) {
        throw new Error("ID d'hébergement et de photo requis")
      }

      return await del(`/accommodations/${accommodationId}/photos/${photoId}`)
    } catch (error) {
      const errorMessage = handleApiError(error, 'Erreur lors de la suppression de la photo')
      throw new Error(errorMessage)
    }
  },

  // NOUVEAU : Vérification de disponibilité
  checkAvailability: async (accommodationId, startDate, endDate) => {
    try {
      if (!accommodationId || !startDate || !endDate) {
        throw new Error('Paramètres manquants pour la vérification de disponibilité')
      }

      return await get(
        `/accommodations/${accommodationId}/availability?start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}`,
      )
    } catch (error) {
      const errorMessage = handleApiError(error, 'Erreur lors de la vérification de disponibilité')
      throw new Error(errorMessage)
    }
  },
}

export default accommodationService
